import { formatDateTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";
import { isEmpty } from "lodash";
import { type ReactNode } from "react";

import { ClipboardScoreActions, reasonDisplayMap } from "../../utils/constants";
import { ShortLeadTimeShiftInfo } from "../Adjustments/ShortLeadTimeShiftInfo";
import { type Consequence } from "./ConsequencePill";

export interface ItemDetails {
  label: string;
  value: string | ReactNode;
  isNegative?: boolean;
}

export interface LogDetails {
  itemDetails: ItemDetails[];
  callout?: React.ReactNode;
}

interface GetCaptionProps {
  action: string;
  reason?: string;
  consequence?: Consequence;
  multiplier?: number;
  usedEmergencyCredit?: boolean;
}

interface GetCalloutProps {
  action: string;
  showShortLeadTimeInfo?: boolean;
}

interface GetLogDetailsProps {
  log: ClipboardScoreLog;
  consequence?: Consequence;
  showShortLeadTimeInfo?: boolean;
  usedEmergencyCredit?: boolean;
  emergencyCreditPointsSaved?: number;
}

function getCallout({ action, showShortLeadTimeInfo }: GetCalloutProps) {
  if (action === ClipboardScoreActions.START_SHIFT_LATE && showShortLeadTimeInfo) {
    return <ShortLeadTimeShiftInfo />;
  }

  return undefined;
}

export function getCaption({
  action,
  consequence,
  reason,
  multiplier,
  usedEmergencyCredit,
}: GetCaptionProps): ReactNode {
  if (usedEmergencyCredit && action === ClipboardScoreActions.CANCEL_SHIFT) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        Same-day cancellation credit applied
      </Text>
    );
  }

  if (usedEmergencyCredit && action === ClipboardScoreActions.START_SHIFT_LATE) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        Lateness credit applied
      </Text>
    );
  }

  if (consequence === "On Restriction") {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        You were already Restricted, so your score didn&apos;t change.
      </Text>
    );
  }

  if (consequence === 0 && isDefined(reason) && !isEmpty(reasonDisplayMap[action][reason])) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        {reasonDisplayMap[action][reason]}
      </Text>
    );
  }

  if (isDefined(multiplier) && multiplier > 1) {
    if (action === ClipboardScoreActions.START_SHIFT_LATE) {
      return (
        <Text variant="body2" color={(theme) => theme.palette.intent?.error.text}>
          This deduction was higher due to recent late arrivals.
        </Text>
      );
    }

    if (action === ClipboardScoreActions.CANCEL_SHIFT) {
      return (
        <Text variant="body2" color={(theme) => theme.palette.intent?.error.text}>
          This deduction was higher due to recent same-day cancels.
        </Text>
      );
    }
  }

  return null;
}

function getEmergencyCreditCallout(
  usedEmergencyCredit: boolean | undefined,
  action: string,
  emergencyCreditPointsSaved: number | undefined
): React.ReactNode {
  const maybeShowCallout =
    usedEmergencyCredit && isDefined(emergencyCreditPointsSaved) && emergencyCreditPointsSaved > 0;

  if (maybeShowCallout && action === ClipboardScoreActions.CANCEL_SHIFT) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        You used your Same-day cancellation credit which saved you {emergencyCreditPointsSaved}{" "}
        points.
      </Text>
    );
  }

  if (maybeShowCallout && action === ClipboardScoreActions.START_SHIFT_LATE) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        You used your Lateness credit which saved you {emergencyCreditPointsSaved} points.
      </Text>
    );
  }

  return undefined;
}

export function getLogDetails({
  log,
  consequence,
  showShortLeadTimeInfo,
  usedEmergencyCredit,
  emergencyCreditPointsSaved,
}: GetLogDetailsProps): LogDetails {
  const itemDetails: ItemDetails[] = [];
  const isCancellation = log.action === ClipboardScoreActions.CANCEL_SHIFT;
  const isLateness = log.action === ClipboardScoreActions.START_SHIFT_LATE;
  const isNegativeAdjustment =
    isDefined(log.clipboardScoreAdjustment) && log.clipboardScoreAdjustment < 0;
  const showCancelledAtInRed = isCancellation && usedEmergencyCredit;
  const showClockedInAtInRed = isLateness && usedEmergencyCredit;

  if (isDefined(log.facilityName)) {
    itemDetails.push({
      label: "Workplace",
      value: log.facilityName,
    });
  }

  // we prefer target arrival time over shift start time
  // only fall back to shift start time if target arrival time is not defined
  if (isDefined(log.targetArrivalTime)) {
    itemDetails.push({
      label: "Target arrival time",
      value: formatDateTime(parseISO(log.targetArrivalTime)),
    });
  } else if (isDefined(log.shiftStart)) {
    itemDetails.push({
      label: "Shift start time",
      value: formatDateTime(parseISO(log.shiftStart)),
    });
  }

  if (isDefined(log.clockInTime)) {
    itemDetails.push({
      label: "Clocked in at",
      value: formatDateTime(parseISO(log.clockInTime)),
      isNegative: showClockedInAtInRed,
    });
  } else {
    itemDetails.push({
      label: isCancellation ? "Cancelled at" : "Recorded at",
      value: formatDateTime(parseISO(log.createdAt)),
      isNegative: showCancelledAtInRed,
    });
  }

  if (
    isDefined(log.clipboardScoreUpdatedScore) &&
    isDefined(log.clipboardScoreAdjustment) &&
    log.clipboardScoreAdjustment !== 0 &&
    consequence !== "On Restriction"
  ) {
    itemDetails.push({
      label: "Score before/after",
      value: (
        <Stack direction="row" spacing={2} alignItems="center">
          <Text inline variant="body2">
            {log.clipboardScoreUpdatedScore - log.clipboardScoreAdjustment} points
          </Text>
          <CbhIcon type="arrow-right" size="xSmall" />
          <Text
            inline
            variant="body2"
            color={(theme) => (isNegativeAdjustment ? theme.palette.intent?.error.text : undefined)}
          >
            {log.clipboardScoreUpdatedScore} points
          </Text>
        </Stack>
      ),
    });
  }

  const emergencyCreditCallout = getEmergencyCreditCallout(
    usedEmergencyCredit,
    log.action,
    emergencyCreditPointsSaved
  );
  const shortLeadTimeCallout = getCallout({ action: log.action, showShortLeadTimeInfo });

  return {
    itemDetails,
    callout: emergencyCreditCallout ?? shortLeadTimeCallout,
  };
}
